import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import {
    AlertTriangle,
    ExternalLink,
    RotateCcw,
    Tag,
    Trash2,
    X,
} from "lucide-preact";
import {
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    Button,
    ConfirmModal,
} from "@mrflark/ui";
import {
    splClient,
} from "../../api/client.ts";
import {
    useAuthStore,
} from "../../stores/authStore.ts";
import {
    TagInput,
} from "../tags/TagInput.tsx";

type BulkActionsBarProps = {
    selectedIds: string[];
    onClearSelection: () => void;
    isDeleted: boolean;
    onActionComplete?: () => void;
};

export const BulkActionsBar = ({
    selectedIds,
    onClearSelection,
    isDeleted,
    onActionComplete,
}: BulkActionsBarProps) => {
    const [showTagInput, setShowTagInput] = useState(false);
    const [tagAction, setTagAction] = useState<"add" | "remove">("add");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmHardDelete, setConfirmHardDelete] = useState(false);
    const queryClient = useQueryClient();

    // Get permissions
    const {
        canTag,
        canDelete,
        canHardDelete,
    } = useAuthStore();

    const count = selectedIds.length;

    // Bulk tag mutation
    const tagMutation = useMutation({
        mutationFn: ({
            tag,
            action,
        }: { tag: string; action: "add" | "remove"; }) =>
            action === "add"
                ? splClient.bulkAddTag(selectedIds, tag)
                : splClient.bulkRemoveTag(selectedIds, tag),
        onSuccess: (data, variables) => {
            toast.success(
                `${
                    variables.action === "add"
                        ? "Added"
                        : "Removed"
                } "${variables.tag}" ${
                    variables.action === "add" ? "to" : "from"
                } ${data.updated_count} item(s)`,
            );
            queryClient.invalidateQueries({
                queryKey: ["manageItems"],
            });
            setShowTagInput(false);
            onActionComplete?.();
        },
        onError: () => {
            toast.error("Failed to update tags");
        },
    });

    // Bulk delete mutation
    const deleteMutation = useMutation({
        mutationFn: () =>
            splClient.bulkAction({
                action: "delete",
                ids: selectedIds,
            }),
        onSuccess: (data) => {
            const successCount = data.results.success.length;
            toast.success(`Deleted ${successCount} item(s)`, {
                duration: 5000,
            });
            queryClient.invalidateQueries({
                queryKey: ["manageItems"],
            });
            onClearSelection();
            setConfirmDelete(false);
            onActionComplete?.();
        },
        onError: () => {
            toast.error("Failed to delete items");
        },
    });

    // Bulk restore mutation
    const restoreMutation = useMutation({
        mutationFn: () =>
            splClient.bulkAction({
                action: "restore",
                ids: selectedIds,
            }),
        onSuccess: (data) => {
            const successCount = data.results.success.length;
            toast.success(`Restored ${successCount} item(s)`);
            queryClient.invalidateQueries({
                queryKey: ["manageItems"],
            });
            onClearSelection();
            onActionComplete?.();
        },
        onError: () => {
            toast.error("Failed to restore items");
        },
    });

    // Bulk hard delete mutation
    const hardDeleteMutation = useMutation({
        mutationFn: () =>
            splClient.bulkAction({
                action: "hard_delete",
                ids: selectedIds,
            }),
        onSuccess: (data) => {
            const successCount = data.results.success.length;
            toast.success(`Permanently deleted ${successCount} item(s)`);
            queryClient.invalidateQueries({
                queryKey: ["manageItems"],
            });
            onClearSelection();
            setConfirmHardDelete(false);
            onActionComplete?.();
        },
        onError: () => {
            toast.error("Failed to permanently delete items");
        },
    });

    const handleTagAction = (tag: string) => {
        tagMutation.mutate({
            tag,
            action: tagAction,
        });
    };

    if (count === 0) return null;

    return (
        <>
            <div className="fixed bottom-0 left-0 right-0 bg-bg-secondary border-t border-border p-4 z-40">
                <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                    {/* Selection info */}
                    <div className="flex items-center gap-3">
                        <span className="text-text-primary font-medium">
                            {count} item{count !== 1 ? "s" : ""} selected
                        </span>
                        <button
                            type="button"
                            onClick={onClearSelection}
                            className="text-text-secondary hover:text-text-primary transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Open the single selected item in a new viewer tab */}
                    {count === 1 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                globalThis.open(
                                    `/?id=${selectedIds[0]}`,
                                    "_blank",
                                )}
                            className="gap-1"
                            data-testid="bulk-open-viewer"
                        >
                            <ExternalLink size={16} />
                            Open in viewer (new tab)
                        </Button>
                    )}

                    {/* Tag actions - only show if user can tag */}
                    {canTag() && (
                        <div className="flex items-center gap-2">
                            {showTagInput
                                ? (
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={tagAction}
                                            onChange={(e) =>
                                                setTagAction(
                                                    (e.target as HTMLInputElement)
                                                        .value as
                                                            | "add"
                                                            | "remove",
                                                )}
                                            className="input py-1.5"
                                        >
                                            <option value="add">Add tag</option>
                                            <option value="remove">
                                                Remove tag
                                            </option>
                                        </select>
                                        <div className="w-48">
                                            <TagInput
                                                onAddTag={handleTagAction}
                                                placeholder="Select tag..."
                                                placement="top"
                                            />
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                setShowTagInput(false)}
                                            disabled={tagMutation.isPending}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )
                                : (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setShowTagInput(true)}
                                        className="gap-1"
                                    >
                                        <Tag size={16} />
                                        Manage Tags
                                    </Button>
                                )}
                        </div>
                    )}

                    {/* Delete/Restore actions - only show if user has permission */}
                    <div className="flex items-center gap-2">
                        {isDeleted
                            ? (
                                <>
                                    {canDelete() && (
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() =>
                                                restoreMutation.mutate()}
                                            isLoading={restoreMutation
                                                .isPending}
                                            className="gap-1"
                                        >
                                            <RotateCcw size={16} />
                                            Restore
                                        </Button>
                                    )}
                                    {canHardDelete() && (
                                        <Button
                                            variant="error"
                                            size="sm"
                                            onClick={() =>
                                                setConfirmHardDelete(true)}
                                            className="gap-1"
                                        >
                                            <AlertTriangle size={16} />
                                            Delete Permanently
                                        </Button>
                                    )}
                                </>
                            )
                            : (
                                canDelete() && (
                                    <Button
                                        variant="error"
                                        size="sm"
                                        onClick={() => setConfirmDelete(true)}
                                        className="gap-1"
                                    >
                                        <Trash2 size={16} />
                                        Delete
                                    </Button>
                                )
                            )}
                    </div>
                </div>
            </div>

            {/* Confirm delete modal */}
            <ConfirmModal
                isOpen={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={() => deleteMutation.mutate()}
                title="Delete Items"
                message={`Are you sure you want to delete ${count} item${
                    count !== 1 ? "s" : ""
                }? They can be restored from the deleted items view.`}
                confirmText="Delete"
                variant="danger"
                isLoading={deleteMutation.isPending}
            />

            {/* Confirm hard delete modal */}
            <ConfirmModal
                isOpen={confirmHardDelete}
                onClose={() => setConfirmHardDelete(false)}
                onConfirm={() => hardDeleteMutation.mutate()}
                title="Permanently Delete Items"
                message={`Are you sure you want to PERMANENTLY delete ${count} item${
                    count !== 1 ? "s" : ""
                }? This action cannot be undone and will remove the files from storage.`}
                confirmText="Delete Permanently"
                variant="danger"
                isLoading={hardDeleteMutation.isPending}
            />
        </>
    );
};
